import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import NoAccessPage from "./NoAccessPage";
import "react-datepicker/dist/react-datepicker.css";
import PaginationButtonToolbar from "../components/PaginatedButtonToolbar";
import CustomDateInputs from "../components/CustomDateInputs";
import {
  formatDateAndTimeOnline,
  formatDateAndTimeHourly,
  formatDateDaily,
} from "../components/DateAndTimeFunctions";
import useJWTInterceptors from "../authentication/useJWTInterceptors";
import SoundChartsPageBarTypeLEQ from "../components/SoundChartsPageBarTypeLEQ";
import SoundChartsPageLineTypeLEQ from "../components/SoundChartsPageLineTypeLEQ";
import NotFound from "./NotFound";

const SoundStationFullDataPaginated = () => {
  const isLoggedIn = useSelector((state) => state.login.accessToken);
  const { theRouteUrl } = useParams();
  const information = useSelector((state) => state.information);
  const informationData = information.informationData;
  const informationError = information.informationError;
  const informationLoading = information.informationLoading;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [device, setDevice] = useState();
  const [deviceId, setDeviceId] = useState();
  const [currentPageData, setCurrentPageData] = useState({});
  const [currentChartLabels, setCurrentChartLabels] = useState({});
  const [pageNumber, setPageNumber] = useState(1);
  const [totalPageNumber, setTotalPageNumber] = useState(0);
  const [totalDataCount, setTotalDataCount] = useState(0);
  const [count, setCount] = useState(0);
  const [soundDataType, setSoundDataType] = useState("spl");
  const [parameter, setParameter] = useState(
    soundDataType === "spl" ? "spl" : "leq"
  );
  const [selectedSoundDataType, setSelectedSoundDataType] = useState("spl");
  const [soundChartType, setSoundChartType] = useState("Line");
  const [pageIsValid, setPageIsValid] = useState(true);
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  yesterday.setHours(0, 0, 0, 0);
  const [selectedStartDateAndTime, setSelectedStartDateAndTime] =
    useState(yesterday);
  const [selectedEndDateAndTime, setSelectedEndDateAndTime] = useState(
    new Date()
  );
  const baseURL = process.env.REACT_APP_BASE_URL;

  useEffect(() => {
    if (!informationLoading && !informationError && informationData) {
      const theSpecificStation = informationData.find(
        (x) => x.slug === theRouteUrl
      );
      setPageIsValid(!!theSpecificStation);
      if (theSpecificStation) {
        setDevice("OSPL");
        const deviceNumber = theSpecificStation.device_id;
        setDeviceId(deviceNumber);
      }
    }
  }, [informationLoading, informationError, informationData, theRouteUrl]);

  const historyApiUrl = useMemo(() => {
    if (!device || !deviceId || !baseURL) return null;

    let startDate, endDate, granularity;

    switch (soundDataType) {
      case "spl":
        startDate = formatDateAndTimeOnline(selectedStartDateAndTime);
        endDate = formatDateAndTimeOnline(selectedEndDateAndTime);
        granularity = 0;
        break;
      case "hourly":
        startDate = formatDateAndTimeHourly(selectedStartDateAndTime);
        endDate = formatDateAndTimeHourly(selectedEndDateAndTime);
        granularity = 1;
        break;
      case "daily":
        startDate = formatDateDaily(selectedStartDateAndTime);
        endDate = formatDateDaily(selectedEndDateAndTime);
        granularity = 2;
        break;
      case "nighttime":
        startDate = formatDateAndTimeHourly(selectedStartDateAndTime);
        endDate = formatDateAndTimeHourly(selectedEndDateAndTime);
        granularity = 3;
        break;
      case "daytime":
        startDate = formatDateAndTimeHourly(selectedStartDateAndTime);
        endDate = formatDateAndTimeHourly(selectedEndDateAndTime);
        granularity = 4;
        break;
      default:
        return null;
    }

    return `${baseURL}history/${
      soundDataType === "spl" ? "OSPL" : "LEQ"
    }/${deviceId}/${granularity}?start_date=${startDate}&end_date=${endDate}&page=${pageNumber}`;
  }, [
    device,
    deviceId,
    baseURL,
    soundDataType,
    selectedStartDateAndTime,
    selectedEndDateAndTime,
    pageNumber,
    pageIsValid,
  ]);

  // Utility function for date formatting
  const convertToJalali = useCallback((x) => {
    if (x) {
      const A = x.slice(0, 10);
      const B = x.slice(11, 19);
      const C = new Date(A);
      const D = new Intl.DateTimeFormat("fa-IR").format(C);
      const E = D.concat(" ", "(", B, ")");
      return E;
    }
  }, []);

  const getChartData = async () => {
    setLoading(true);
    setSelectedSoundDataType(soundDataType);
    setLoading(true);
    setError(false);

    let allData = [];
    let currentPage = 1;
    let totalPages = 1; // Default to 1, will be updated after first request

    try {
      while (currentPage <= totalPages) {
        const response = await useJWTInterceptors.get(
          `${historyApiUrl}&page=${currentPage}`
        );

        allData = [...allData, ...response.data.results];

        if (currentPage === 1) {
          // Set total pages based on the first response
          totalPages = Math.ceil((response.data.count || 0) / 50);
        }

        currentPage++; // Move to the next page
      }
      if (Array.isArray(allData) && allData.length > 0) {
      }

      const reversedAllData = [...allData].reverse();
      const reversedLabels = [...reversedAllData].map((data) =>
        convertToJalali(data.ccreated_datetime)
      );
      setCurrentPageData(reversedAllData);
      setCurrentChartLabels(reversedLabels);
      setTotalDataCount(reversedAllData.length);
      setTotalPageNumber(totalPages);
    } catch (error) {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {}, [
    soundDataType,
    parameter,
    currentChartLabels?.length,
    currentPageData?.length,
    count,
    loading,
  ]);

  // Fetch latest data only when `count` or `pageNumber` changes
  useEffect(() => {
    getChartData();
  }, [count]); // Fetch only when `count` or `pageNumber` changes

  if (!isLoggedIn) {
    return <NoAccessPage />;
  }
  if (!pageIsValid) {
    <NotFound />;
  }
  if (isLoggedIn && pageIsValid) {
    return (
      <div className="container-fluid p-2 text-medium">
        <div className="row">
          <CustomDateInputs
            type="soundChart"
            startDateAndTime={selectedStartDateAndTime}
            endDateAndTime={selectedEndDateAndTime}
            setSelectedStartDateAndTime={setSelectedStartDateAndTime}
            setSelectedEndDateAndTime={setSelectedEndDateAndTime}
            setCount={setCount}
            soundDataType={soundDataType}
            setSoundDataType={setSoundDataType}
            count={count}
            setPageNumber={setPageNumber}
            parameter={parameter}
            setParameter={setParameter}
            setSoundChartType={setSoundChartType}
          />
          <div className="col-lg-8 col-xl-9 my-5">
            {Array.isArray(currentPageData) && currentPageData.length > 0 && (
              <>
                {soundChartType === "Line" ? (
                  <SoundChartsPageLineTypeLEQ
                    soundDataType={soundDataType}
                    parameter={parameter}
                    chartData={currentPageData}
                    chartLabels={currentChartLabels}
                    count={count}
                    loading={loading}
                  />
                ) : (
                  <SoundChartsPageBarTypeLEQ
                    soundDataType={soundDataType}
                    parameter={parameter}
                    chartData={currentPageData}
                    chartLabels={currentChartLabels}
                    count={count}
                    loading={loading}
                  />
                )}
              </>
            )}
          </div>
        </div>
      </div>
    );
  }
};

export default SoundStationFullDataPaginated;
